/**
 * The curated starter set (M2.7.1, PRD R1.4).
 *
 * **"A curated starter set by interest, so a new account is never empty" —
 * curated by us, not algorithmically generated.** PRD §3.2's attention
 * contract applies to onboarding as much as to the timeline: a
 * recommendation engine at the front door would contradict the entire
 * product, and there is no engine here. Twenty-five feeds, chosen by a
 * person, each one measured before it was committed.
 *
 * ## Why a curated list rather than a search box
 *
 * Somebody arriving without an OPML file cannot be asked "which feeds do you
 * want?" — that is the question they came here unable to answer. Before this
 * existed, a new account returned an empty subscription list, a zero count and
 * an empty timeline, and the only way forward was to already know a URL.
 *
 * ## The selection rules, and the two that are not obvious
 *
 * A feed belongs here only if all five hold. The first three are about quality
 * and the last two were learned from the code:
 *
 * 1. **A publisher, not an aggregator.** Hacker News and Reddit are other
 *    people's timelines; this product is trying to replace that.
 * 2. **Openly readable.** A hard paywall delivers titles and nothing else,
 *    which is a poor first impression even when the publication is excellent.
 *    The Economist and the FT were dropped for this; the seed corpus keeps a
 *    paywalled feed for the *parser*, which is a different job.
 * 3. **Published within 90 days**, which is not an arbitrary freshness rule:
 *    it is the same window the onboarding backfill uses, so a feed that fails
 *    it would be subscribed successfully and then show nothing. Joel on
 *    Software (last post 1,375 days ago) and Wait But Why (303) were dropped
 *    for exactly that reason.
 * 4. **Items must carry a parseable date.** APOD is a lovely feed and it is
 *    *not* here: it publishes no dates at all, so the backfill can date none
 *    of its items and a new subscriber sees an empty feed that looks broken.
 *    Found by probing rather than by reading.
 * 5. **Under about 500 KB.** A first subscribe ingests inline (OI-1), so the
 *    feed's size is time the new user spends watching a spinner. Dan Luu's
 *    excellent blog is 6.4 MB and stays in the seed corpus, where being
 *    enormous is the point.
 *
 * ## Keeping it honest
 *
 * `starter-set.test.ts` checks the structure — categories non-empty, URLs
 * well-formed, no duplicates — and `npm run verify:starter-set` checks the
 * feeds themselves against rules 3, 4 and 5 over the network. The test cannot
 * do the second job without making the suite depend on twenty-five other
 * people's servers, and the script cannot run in CI for the same reason. Run
 * it before launch, and whenever this file is edited.
 *
 * Measurements below are from 2026-09-24.
 */

export interface StarterFeed {
  readonly title: string;
  readonly url: string;
  /** One line on why somebody might want it. Shown in the picker. */
  readonly description: string;
}

export interface StarterCategory {
  readonly id: string;
  readonly name: string;
  /** What this category is for, in the picker. */
  readonly description: string;
  readonly feeds: readonly StarterFeed[];
}

export const STARTER_SET: readonly StarterCategory[] = [
  {
    id: 'technology',
    name: 'Technology',
    description: 'What is being built, and what it is doing to everything else.',
    feeds: [
      {
        title: 'The Verge',
        url: 'https://www.theverge.com/rss/index.xml',
        // Atom, 10 items, same-day, 30 KB.
        description: 'Technology, culture and the overlap, several times a day.',
      },
      {
        title: 'Ars Technica',
        url: 'https://arstechnica.com/feed/',
        // RSS 2.0, 20 items, same-day, 77 KB. The publisher's own URL rather
        // than the FeedBurner alias, which merely redirects to this.
        description: 'Long, technical, and unusually willing to show its work.',
      },
      {
        title: 'Wired',
        url: 'https://www.wired.com/feed/rss',
        // RSS 2.0, 50 items, same-day, 43 KB.
        description: 'Reporting on technology as a political and social force.',
      },
      {
        title: '404 Media',
        url: 'https://www.404media.co/rss/',
        // RSS 2.0, 15 items, same-day, 112 KB.
        description:
          'Journalist-owned, and consistently ahead on the internet’s worst corners.',
      },
      {
        title: 'Daring Fireball',
        url: 'https://daringfireball.net/feeds/main',
        // Atom, 48 items, same-day, 231 KB.
        description: 'One person, twenty years, mostly Apple and mostly right.',
      },
    ],
  },
  {
    id: 'science',
    name: 'Science',
    description: 'Research and discovery, written for people outside the lab.',
    feeds: [
      {
        title: 'Quanta Magazine',
        url: 'https://www.quantamagazine.org/feed/',
        // RSS 2.0, 5 items, 1 day, 11 KB.
        description: 'Mathematics and physics explained without being made shallow.',
      },
      {
        title: 'Scientific American',
        url: 'https://www.scientificamerican.com/platform/syndication/rss/',
        // RSS 2.0, 50 items, same-day, 29 KB.
        description:
          'A hundred and seventy years of explaining research to everyone else.',
      },
      {
        title: 'Phys.org',
        url: 'https://phys.org/rss-feed/',
        // RSS 2.0, 30 items, same-day, 31 KB.
        description: 'A steady stream of new papers, summarised.',
      },
      {
        title: 'Nature',
        url: 'https://www.nature.com/nature.rss',
        // RDF (RSS 1.0), 75 items, 1 day, 132 KB.
        description: 'The journal itself: research, news and comment.',
      },
      {
        title: 'NASA',
        url: 'https://www.nasa.gov/feed/',
        // RSS 2.0, 10 items, same-day, 199 KB.
        description: 'Missions, images and results, from the source.',
      },
    ],
  },
  {
    id: 'programming',
    name: 'Programming',
    description: 'People who build software, writing about how and why.',
    feeds: [
      {
        title: 'Simon Willison',
        url: 'https://simonwillison.net/atom/everything/',
        // Atom, 30 items, same-day, 80 KB.
        description: 'Daily notes on tools, databases and language models.',
      },
      {
        title: 'Martin Fowler',
        url: 'https://martinfowler.com/feed.atom',
        // Atom, 30 items, same-day, 170 KB.
        description: 'Design and architecture, patiently argued.',
      },
      {
        title: 'overreacted',
        url: 'https://overreacted.io/rss.xml',
        // RSS 2.0, 59 items, 7 days, 24 KB.
        description: 'Dan Abramov on how things actually work underneath.',
      },
      {
        title: 'Julia Evans',
        url: 'https://jvns.ca/atom.xml',
        // Atom, 20 items, 66 days, 235 KB. Inside the 90-day rule, and a
        // reminder that a good blog is not a daily one.
        description: 'Debugging, networking and unix, explained by drawing them.',
      },
      {
        title: 'The Pragmatic Engineer',
        url: 'https://blog.pragmaticengineer.com/rss/',
        // RSS 2.0, 15 items, same-day, 235 KB.
        description: 'How software is built inside real companies.',
      },
    ],
  },
  {
    id: 'culture',
    name: 'Culture and ideas',
    description: 'Art, history and long arguments worth the time.',
    feeds: [
      {
        title: 'The Marginalian',
        url: 'https://www.themarginalian.org/feed/',
        // RSS 2.0, 20 items, same-day, 257 KB.
        description: 'Literature, science and what they have to say to each other.',
      },
      {
        title: 'Colossal',
        url: 'https://www.thisiscolossal.com/feed/',
        // RSS 2.0, 10 items, same-day, 101 KB.
        description: 'Visual art and design, image-heavy and quietly curated.',
      },
      {
        title: 'Aeon',
        url: 'https://aeon.co/feed.rss',
        // RSS 2.0, 20 items, 1 day, 19 KB.
        description: 'Essays on philosophy, psychology and society.',
      },
      {
        title: 'kottke.org',
        url: 'https://feeds.kottke.org/main',
        /*
         * Atom, 60 items, same-day, 89 KB.
         *
         * Three URLs, two of which do not work. `/main/index.rdf` — the one in
         * every old blogroll — now 404s. `kottke.org/index.xml` looks right and
         * `curl` follows it happily, but it 301s to **http**, and the
         * SSRF-safe client refuses an insecure redirect by design (M1.1), so
         * subscribing to it fails with "no feed found". This is the endpoint
         * the redirect is trying to reach, over https, directly.
         *
         * Caught by `npm run verify:starter-set` reporting the redirect, which
         * is the entire reason that script prints them.
         */
        description: 'The original weblog, still finding things since 1998.',
      },
      {
        title: 'The Public Domain Review',
        url: 'https://publicdomainreview.org/rss.xml',
        // RSS 2.0, 100 items, 1 day, 84 KB.
        description: 'Curiosities from the archives, out of copyright and back in view.',
      },
    ],
  },
  {
    id: 'world-news',
    name: 'World news',
    description: 'Wire services and world desks. Openly readable, no paywalls.',
    feeds: [
      {
        title: 'BBC News — World',
        url: 'https://feeds.bbci.co.uk/news/world/rss.xml',
        // RSS 2.0, 35 items, same-day, 26 KB.
        description: 'The world desk, updated continuously.',
      },
      {
        title: 'The Guardian — World',
        url: 'https://www.theguardian.com/world/rss',
        // RSS 2.0, 45 items, same-day, 151 KB.
        description: 'International reporting, free to read.',
      },
      {
        title: 'NPR News',
        url: 'https://feeds.npr.org/1004/rss.xml',
        // RSS 2.0, 10 items, same-day, 13 KB.
        description: 'US public radio’s main news feed.',
      },
      {
        title: 'Al Jazeera',
        url: 'https://www.aljazeera.com/xml/rss/all.xml',
        // RSS 2.0, 25 items, same-day, 17 KB.
        description: 'A different centre of gravity from the Anglo-American press.',
      },
      {
        title: 'Deutsche Welle',
        url: 'https://rss.dw.com/rdf/rss-en-all',
        // RDF (RSS 1.0), 138 items, same-day, 121 KB.
        description: 'Germany’s international broadcaster, in English.',
      },
    ],
  },
];

/** Every feed in the set, flattened. */
export const starterFeeds = (): readonly (StarterFeed & { category: string })[] =>
  STARTER_SET.flatMap((category) =>
    category.feeds.map((feed) => ({ ...feed, category: category.id })),
  );

/** A category by id, for resolving a picker selection. */
export const starterCategory = (id: string): StarterCategory | undefined =>
  STARTER_SET.find((category) => category.id === id);
